import { requester } from '@/api/axios';

import { apiConst, keyConst } from '@/constants';

import {
  ApiResponse,
  CompanyBasicInfo,
  CompanyUser,
  ForgotPasswordResponse,
  GetPermissionsForm,
  ITwoFactorLoginForm,
  ITwoFactorRequiredResponse,
  ITwoFactorSetupResponse,
  ITwoFactorSetupVerifyRequest,
  ITwoFactorStatusResponse,
  InviteUserForm,
  ResetPasswordResponse,
  SetCompanyRequest,
  SignInForm,
  SignUpForm,
  UpdateProfileRequest,
  UserWithCompanyResponse,
} from '@/interface';
import { IClientPreviewResponse } from '@/interface/client-preview.interface';
import { IUpdateUserProfileRequest } from '@/interface/user-profile.interface';

export interface UserInfoPayload {
  userId: string;
  companyId?: string;
  projectId?: string;
  email?: string;
  username?: string;
  role?: string;
  userType?: string;
  permission?: Record<string, 0 | 1>;
}

export interface AuthTokenResponse {
  UserInfo: UserInfoPayload;
  access_token: string;
  message?: string;
  twoFactorEnabled?: boolean;
  rememberMe?: boolean;
}

export type RegistrationResponse = AuthTokenResponse | ITwoFactorSetupResponse;

export type LoginResponse =
  | {
      access_token: string;
      rememberMe?: boolean;
    }
  | ITwoFactorRequiredResponse
  | ITwoFactorSetupResponse;

export interface TokenResponse {
  access_token: string;
  rememberMe?: boolean;
}

export interface PermissionsResponse {
  projectPermissions?: Record<string, 0 | 1>;
  companyPermissions?: Record<string, 0 | 1>;
  userPermissions?: Record<string, 0 | 1>;
}

export interface SetCompanyResponse {
  access_token: string;
  userInfo?: {
    companyPermissions?: Record<string, 0 | 1>;
    role?: string;
  };
}

export interface AcceptInviteResponse {
  status: string;
  email: string;
  companyName: string | null;
  projectId?: string | null;
}

export const previewClient = async (clientUserId: string): Promise<ApiResponse<IClientPreviewResponse>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'post',
    url: `${apiConst.authPreviewClient}/${clientUserId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

// Login function
export const login = async (data: SignInForm): Promise<ApiResponse<LoginResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authLogin,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const logoutRequest = async (accessToken?: string | null): Promise<ApiResponse<{ message: string }>> => {
  const config = {
    method: 'post',
    url: apiConst.authLogout,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const refreshAuthToken = async (): Promise<ApiResponse<TokenResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authRefresh,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
// getPermissionsRequest function
export const getPermissions = async (data: GetPermissionsForm): Promise<ApiResponse<PermissionsResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.getAllPermissions,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

// Sign up function
export const signUpOwner = async (data: SignUpForm): Promise<ApiResponse<RegistrationResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authSignUpOwner,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};

export const signUpUser = async (data: SignUpForm): Promise<ApiResponse<RegistrationResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authSignUp,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};

export const verifyTwoFactorLogin = async (data: ITwoFactorLoginForm): Promise<ApiResponse<TokenResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authTwoFactorLoginVerify,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const startTwoFactorSetup = async (): Promise<ApiResponse<ITwoFactorSetupResponse>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'post',
    url: apiConst.authTwoFactorSetup,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const verifyTwoFactorSetup = async (data: ITwoFactorSetupVerifyRequest): Promise<ApiResponse<AuthTokenResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authTwoFactorSetupVerify,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const disableTwoFactor = async (): Promise<ApiResponse<ITwoFactorStatusResponse>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'patch',
    url: apiConst.userTwoFactorDisable,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const setCompany = async (data: SetCompanyRequest): Promise<ApiResponse<SetCompanyResponse>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'patch',
    url: apiConst.setCompany,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const getAllCompaniesList = async (): Promise<ApiResponse<CompanyBasicInfo[]>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'get',
    url: `${apiConst.getCompaniesByToken}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  return await requester(config);
};
export const getUserWithCompany = async (): Promise<ApiResponse<UserWithCompanyResponse>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'get',
    url: `${apiConst.userDetails}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  return await requester(config);
};

export const updateMyProfile = async (data: IUpdateUserProfileRequest): Promise<ApiResponse<unknown>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'patch',
    url: `${apiConst.userDetails}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const updateMyProfilePhoto = async (file: File): Promise<ApiResponse<UserWithCompanyResponse>> => {
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    method: 'patch',
    url: apiConst.userProfilePhoto,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  };
  return await requester(config);
};
export const getCompany = async (id: string): Promise<ApiResponse<CompanyBasicInfo>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getCompany}/${id}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  };
  return await requester(config);
};

export const getCompanyUser = async (id?: string, projectId?: string, search?: string): Promise<ApiResponse<CompanyUser[]>> => {
  const queryParams = new URLSearchParams();

  if (id) {
    queryParams.append('companyId', id);
  }

  if (projectId) {
    queryParams.append('projectId', projectId);
  }

  if (search) {
    queryParams.append('search', search);
  }

  const config = {
    method: 'get',
    url: `${apiConst.user}?${queryParams.toString()}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

// Update profile function
export const updateProfile = async (id: string, data: UpdateProfileRequest): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'put',
    url: `${apiConst.updateUser}/${id}`,
    headers: {
      userid: localStorage.getItem(keyConst.userId),
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};

export const getUserById = async (id: string) => {
  const config = {
    method: 'get',
    url: `${apiConst.user}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const updateUser = async (id: string, data: InviteUserForm): Promise<ApiResponse<CompanyUser>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.user}/${id}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// Delete user function
export const deleteUser = async (id: string): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.user}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export const deleteUserFromCompany = async (id: string): Promise<ApiResponse<{ message?: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.user}/${id}/company`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

// Invite User
export const inviteUser = async (data: InviteUserForm): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.sentInvite,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// // accept invite
export const acceptInviteLink = async (token: string): Promise<ApiResponse<AcceptInviteResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.acceptInvite}/${token}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const forgotPassword = async (email: string): Promise<ApiResponse<ForgotPasswordResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.authForgotPassword,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({ email }),
  };
  return await requester(config);
};

export const resetPassword = async (token: string, newPassword: string): Promise<ApiResponse<ResetPasswordResponse>> => {
  const config = {
    method: 'patch',
    url: apiConst.authResetPassword,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({ token, newPassword }),
  };
  return await requester(config);
};
